import { useState, useEffect, useCallback } from "react";

export function useLocalStorage(key, defaultValue) {
  // Initialize state from localStorage or default
  const [value, setValue] = useState(() => {
    try {
      const stored = localStorage.getItem(key);
      return stored !== null ? JSON.parse(stored) : defaultValue;
    } catch (err) {
      console.warn(`Error reading localStorage key "${key}":`, err);
      return defaultValue;
    }
  });

  // Function to update state + localStorage + same-tab events
  const updateValue = useCallback(
    (newValue) => {
      setValue((prev) => {
        const valueToStore = typeof newValue === "function" ? newValue(prev) : newValue;

        try {
          localStorage.setItem(key, JSON.stringify(valueToStore));
        } catch (err) {
          console.warn(`Error setting localStorage key "${key}":`, err);
        }

        // Dispatch custom event for same-tab listeners
        window.dispatchEvent(
          new CustomEvent("localstorage-change", {
            detail: { key, value: valueToStore },
          })
        );

        return valueToStore;
      });
    },
    [key]
  );

  // Listen for changes in other tabs
  useEffect(() => {
    const handleStorage = (event) => {
      if (event.storageArea === localStorage && event.key === key) {
        try {
          const newValue = event.newValue !== null ? JSON.parse(event.newValue) : defaultValue;
          setValue(newValue);
        } catch (err) {
          console.warn(`Error parsing localStorage key "${key}" from storage event:`, err);
        }
      }
    };
    window.addEventListener("storage", handleStorage);
    return () => window.removeEventListener("storage", handleStorage);
  }, [key, defaultValue]);

  // Listen for same-tab custom events (optional if multiple hook instances on the same page)
  useEffect(() => {
    const handleCustomEvent = (event) => {
      if (event.detail.key === key) {
        setValue(event.detail.value);
      }
    };
    window.addEventListener("localstorage-change", handleCustomEvent);
    return () => window.removeEventListener("localstorage-change", handleCustomEvent);
  }, [key]);

  return [value, updateValue];
}
